import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { firstValueFrom, interval, Observable } from 'rxjs';
import { map, shareReplay, startWith, switchMap } from 'rxjs/operators';

export interface MaintenancePlatform {
    Description: string;
    ScheduledStart: string;
    ScheduledEnd: string;
    Enabled: boolean;
}

export interface Maintenance {
    Version: string;
    Description: string;
    ScheduledStart: string;
    ScheduledEnd: string;
    Enabled: boolean;
    Platforms: {
        Backoffice: MaintenancePlatform;
        Frontoffice: MaintenancePlatform;
        Application: MaintenancePlatform;
    };
}

@Injectable({
    providedIn: 'root'
})
export class MaintenanceService {

    private maintenanceUrl = (environment.Integration.ServerConfig.URL + "maintenance.json") || '';
    private checkIntervalMinutes = 10; // Intervalo de verificação em minutos
    private lastFetchedMinute: number | null = null;
    private cachedMaintenanceInfo: Observable<{
        Version: string;
        Description: string;
        ScheduledStart: string;
        ScheduledEnd: string;
        Enabled: boolean;
        UnderMaintenance: boolean;
    }> | null = null;

    constructor(private http: HttpClient) { }

    // Método para buscar o estado de manutenção
    private getMaintenanceStatus(): Observable<Maintenance> {
        const currentMinute = new Date().getMinutes();
        // Verifica se já temos dados em cache para o minuto atual
        if (this.lastFetchedMinute === currentMinute && this.cachedMaintenanceInfo) {
            return this.cachedMaintenanceInfo.pipe(
                map(info => {
                    // Aqui, precisamos retornar um Observable<Maintenance> fictício
                    // pois o cache é do tipo retornado por GetMaintenanceInfo
                    // Esta é uma solução simplificada; pode ser melhorada conforme a necessidade
                    return {
                        Version: info.Version,
                        Description: info.Description,
                        ScheduledStart: info.ScheduledStart,
                        ScheduledEnd: info.ScheduledEnd,
                        Enabled: info.Enabled,
                        Platforms: {} as any // Precisa ser ajustado conforme a estrutura real
                    };
                })
            );
        }

        // Se não houver cache válido, faz a chamada HTTP
        this.lastFetchedMinute = currentMinute;
        return this.http.get<Maintenance>(`${this.maintenanceUrl}?cache=${new Date().getTime()}`).pipe(
            shareReplay(1) // Compartilha o resultado entre múltiplos subscribers
        );
    }

    // Método para obter informações de manutenção com base na plataforma
    public GetMaintenanceInfo(platform?: string): Observable<{
        Version: string;
        Description: string;
        ScheduledStart: string;
        ScheduledEnd: string;
        Enabled: boolean;
        UnderMaintenance: boolean;
    }> {
        const currentMinute = new Date().getMinutes();

        if (this.lastFetchedMinute !== currentMinute) {
            this.cachedMaintenanceInfo = this.getMaintenanceStatus().pipe(
                map((maintenance: Maintenance) => {
                    const now = new Date();
                    const underMaintenance = (environment.ConfigIsProduction && maintenance.Enabled) &&
                        (maintenance?.ScheduledStart && new Date(maintenance?.ScheduledStart).getTime() < now.getTime()) &&
                        (maintenance?.ScheduledEnd && new Date(maintenance?.ScheduledEnd).getTime() > now.getTime());

                    let result = {
                        Version: maintenance.Version,
                        Description: maintenance.Description,
                        ScheduledStart: maintenance.ScheduledStart,
                        ScheduledEnd: maintenance.ScheduledEnd,
                        Enabled: maintenance.Enabled,
                        UnderMaintenance: underMaintenance
                    };

                    // Verifica se a manutenção global está ativa
                    if (maintenance.Enabled) {
                        const start = new Date(maintenance.ScheduledStart);
                        const end = new Date(maintenance.ScheduledEnd);
                        if (now >= start && now <= end) {
                            return result;
                        }
                    }

                    // Verifica se alguma plataforma específica está em manutenção
                    if (platform && maintenance.Platforms[platform as keyof typeof maintenance.Platforms]) {
                        const platformData: MaintenancePlatform = maintenance.Platforms[platform as keyof typeof maintenance.Platforms];
                        const underMaintenancePlatform = (environment.ConfigIsProduction && platformData.Enabled) &&
                            (platformData?.ScheduledStart && new Date(platformData?.ScheduledStart).getTime() < now.getTime()) &&
                            (platformData?.ScheduledEnd && new Date(platformData?.ScheduledEnd).getTime() > now.getTime());

                        if (platformData.Enabled) {
                            result = {
                                Version: maintenance.Version,
                                Description: platformData.Description || maintenance.Description,
                                ScheduledStart: platformData.ScheduledStart,
                                ScheduledEnd: platformData.ScheduledEnd,
                                Enabled: platformData.Enabled,
                                UnderMaintenance: underMaintenancePlatform
                            };
                        }
                    }
                    return result;
                }),
                shareReplay(1) // Cacheia o resultado para múltiplos subscribers
            );
        }

        // Retorna o cache se disponível
        return this.cachedMaintenanceInfo!;
    }


    // Método para verificar periodicamente o estado de manutenção
    public CheckMaintenancePeriodically(platform?: string): Observable<{
        Version: string;
        Description: string;
        ScheduledStart: string;
        ScheduledEnd: string;
        Enabled: boolean;
        UnderMaintenance: boolean;
    }> {
        return interval(this.checkIntervalMinutes * 60 * 1000).pipe(
            startWith(0), // Emite um valor inicial para disparar a primeira verificação imediatamente
            switchMap(() => this.GetMaintenanceInfo(platform))
        );
    }

    /**
     * 
     * @param platform 
     * @returns 
     */
    public async GetCacheMaintenanceInfo(platform?: string): Promise<{
        Version: string;
        Description: string;
        ScheduledStart: string;
        ScheduledEnd: string;
        Enabled: boolean;
        UnderMaintenance: boolean;
    } | null> {
        try {
            if (this.cachedMaintenanceInfo) {
                return firstValueFrom(this.cachedMaintenanceInfo);
            }

            return await firstValueFrom(this.GetMaintenanceInfo(platform));
        } catch (error) {
            console.error('Erro ao obter informações de manutenção:', error);
            return null;
        }

    }
}
